import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Tooltip, Button, Popover } from 'antd';
import { DownOutlined, FolderAddOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useUserContext } from '../../contexts/UserContext';
import AffaireCreateForm from '../affaire/AffaireCreateForm';
import { NAV_GROUPS } from './navConfig.jsx';
import styles from './SidebarNav.module.scss';

// ── Sub-item link ────────────────────────────────────────
const NavItem = ({ to, href, icon, label, onClick }) => {
  const { pathname } = useLocation();

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.navItem}
        onClick={onClick}
      >
        <span className={styles.navItemIconWrap}>{icon}</span>
        <span className={styles.navItemLabel}>{label}</span>
      </a>
    );
  }

  const isActive = pathname === to || (to !== '/' && pathname.startsWith(to + '/')) || pathname === to;

  return (
    <NavLink
      to={to}
      className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
      onClick={onClick}
    >
      <span className={styles.navItemIconWrap}>{icon}</span>
      <span className={styles.navItemLabel}>{label}</span>
    </NavLink>
  );
};
NavItem.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

// ── Popout content shown on hover when sidebar is collapsed ──
const PopoutMenu = ({ group, visibleItems, onNavigate }) => {
  const { pathname } = useLocation();
  return (
    <div className={styles.popout}>
      <p className={styles.popoutTitle}>{group.label}</p>
      {visibleItems.map((item) => {
        if (item.href) {
          return (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.popoutItem}
              onClick={onNavigate}
            >
              <span className={styles.popoutIconWrap}>{item.icon}</span>
              <span>{item.label}</span>
            </a>
          );
        }
        const isActive = pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to + '/'));
        return (
          <NavLink
            key={item.to}
            to={item.to}
            className={`${styles.popoutItem} ${isActive ? styles.popoutItemActive : ''}`}
            onClick={onNavigate}
          >
            <span className={styles.popoutIconWrap}>{item.icon}</span>
            <span>{item.label}</span>
            {item.shortcut && <kbd className={styles.popoutKbd}>{item.shortcut}</kbd>}
          </NavLink>
        );
      })}
    </div>
  );
};
PopoutMenu.propTypes = {
  group: PropTypes.object.isRequired,
  visibleItems: PropTypes.array.isRequired,
  onNavigate: PropTypes.func,
};

// ── Nav Group ────────────────────────────────────────────
const NavGroup = ({ group, collapsed, onNavigate, check, isAdmin }) => {
  const { pathname } = useLocation();

  const visibleItems = group.items.filter(
    (item) =>
      (!item.permission || check(item.permission)) &&
      (!item.adminOnly || isAdmin)
  );

  const isGroupActive = visibleItems.some(
    (item) => item.to && (pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to + '/')))
  );

  const [open, setOpen] = useState(group.defaultOpen || isGroupActive);

  useEffect(() => {
    if (isGroupActive) setOpen(true);
  }, [isGroupActive]);

  if (visibleItems.length === 0) return null;

  // ── Collapsed: icon button + popover ──
  if (collapsed) {
    return (
      <Popover
        placement="rightTop"
        trigger="hover"
        overlayClassName="silos-popout"
        content={
          <PopoutMenu group={group} visibleItems={visibleItems} onNavigate={onNavigate} />
        }
      >
        <button
          className={`${styles.groupIconBtn} ${isGroupActive ? styles.groupIconBtnActive : ''}`}
          aria-label={group.label}
        >
          <span className={styles.groupIconWrap}>{group.icon}</span>
        </button>
      </Popover>
    );
  }

  // ── Expanded: collapsible section ──
  return (
    <div className={styles.group}>
      <button
        className={`${styles.groupHeader} ${isGroupActive ? styles.groupHeaderActive : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.groupHeaderLeft}>
          <span className={styles.groupLabel}>{group.label}</span>
        </span>
        <DownOutlined className={`${styles.groupChevron} ${open ? styles.groupChevronOpen : ''}`} />
      </button>

      <div className={`${styles.groupItems} ${open ? styles.groupItemsOpen : ''}`}>
        {visibleItems.map((item) => (
          <NavItem
            key={item.to || item.href}
            to={item.to}
            href={item.href}
            icon={item.icon}
            label={item.label}
            onClick={onNavigate}
          />
        ))}
      </div>
    </div>
  );
};
NavGroup.propTypes = {
  group: PropTypes.object.isRequired,
  collapsed: PropTypes.bool,
  onNavigate: PropTypes.func,
  check: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
};

// ── Main SidebarNav ──────────────────────────────────────
const SidebarNav = ({ collapsed, onNavigate }) => {
  const { check, currentUser } = useUserContext();
  const navigate = useNavigate();
  const [affaireModal, setAffaireModal] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (currentUser) setReady(true);
  }, [currentUser]);

  const handleCreated = useCallback((affaire) => {
    setAffaireModal(false);
    navigate(`/affaire/${affaire.uid}`);
    onNavigate?.();
  }, [navigate, onNavigate]);

  if (!ready) return null;

  return (
    <nav className={styles.nav}>
      {/* ── CTA Nouveau dossier ── */}
      {check('CREATE-AFFAIRE') && (
        <div className={`${styles.ctaWrap} ${collapsed ? styles.ctaWrapCollapsed : ''}`}>
          <Tooltip title={collapsed ? 'Nouveau dossier' : ''} placement="right">
            <Button
              type="primary"
              icon={<FolderAddOutlined />}
              className={`${styles.ctaBtn} ${collapsed ? styles.ctaBtnCollapsed : ''}`}
              onClick={() => setAffaireModal(true)}
            >
              {!collapsed && 'Nouveau dossier'}
            </Button>
          </Tooltip>
          <AffaireCreateForm
            visible={affaireModal}
            onCancel={() => setAffaireModal(false)}
            onCreate={handleCreated}
          />
        </div>
      )}

      {/* ── Groups ── */}
      <div className={styles.groups}>
        {NAV_GROUPS.map((group) => (
          <NavGroup
            key={group.key}
            group={group}
            collapsed={collapsed}
            onNavigate={onNavigate}
            check={check}
            isAdmin={!!currentUser?.is_admin}
          />
        ))}
      </div>
    </nav>
  );
};

SidebarNav.propTypes = {
  collapsed: PropTypes.bool,
  onNavigate: PropTypes.func,
};

export default SidebarNav;
